import React, { useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';
import { AuthContext } from '../contexts/AuthContext';
import FloatingInput from './ui/FloatingInput';
import SegmentedControl from './ui/SegmentedControl';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STEPS = [
  { id: 1, label: 'Account type' },
  { id: 2, label: 'Credentials' },
  { id: 3, label: 'Profile' },
  { id: 4, label: 'Done' },
];

const ACCOUNT_TYPE_OPTIONS = [
  {
    value: 'user',
    label: 'Personal',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    value: 'business',
    label: 'Business',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    value: 'organization',
    label: 'Organization',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

// ---------------------------------------------------------------------------
// Password strength helper
// ---------------------------------------------------------------------------

function calculatePasswordStrength(password) {
  if (!password) return { score: 0, text: '', level: 'none', width: '0%', checks: {} };

  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };

  let score = 0;
  if (checks.length) score += 20;
  if (checks.uppercase) score += 20;
  if (checks.lowercase) score += 20;
  if (checks.number) score += 20;
  if (checks.special) score += 20;
  if (password.length >= 12) score += 10;
  if (password.length >= 16) score += 10;

  let text, level, width;
  if (score < 40) {
    text = 'Weak';
    level = 'weak';
    width = '25%';
  } else if (score < 60) {
    text = 'Fair';
    level = 'fair';
    width = '50%';
  } else if (score < 80) {
    text = 'Good';
    level = 'good';
    width = '75%';
  } else {
    text = 'Strong';
    level = 'strong';
    width = '100%';
  }

  return { score, text, level, width, checks };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Numbered step indicator with connecting progress line. */
function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center mb-8" role="list" aria-label="Registration steps">
      {STEPS.slice(0, totalSteps).map((step, idx) => {
        const isCompleted = currentStep > step.id;
        const isActive = currentStep === step.id;
        const isLast = idx === totalSteps - 1;

        return (
          <React.Fragment key={step.id}>
            <div
              className="flex flex-col items-center"
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
            >
              <div
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-body-sm font-bold transition-all duration-300',
                  isCompleted && 'bg-primary-500 text-white',
                  isActive && 'bg-primary-500 text-white ring-4 ring-primary-500/20',
                  !isCompleted && !isActive && 'bg-surface-200 text-surface-400'
                )}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              <span
                className={clsx(
                  'mt-1 text-caption font-medium hidden sm:block',
                  isActive ? 'text-primary-600' : 'text-surface-400'
                )}
              >
                {step.label}
              </span>
            </div>

            {/* Connecting line (not after the last step) */}
            {!isLast && (
              <div className="flex-1 mx-2 mb-4 sm:mb-5">
                <div className="h-0.5 w-full bg-surface-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-500"
                    style={{ width: isCompleted ? '100%' : '0%' }}
                  />
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Animated password strength bar and checklist. */
function PasswordStrength({ password }) {
  const { text, level, width, checks } = calculatePasswordStrength(password);

  const barColor = {
    none: 'bg-surface-200',
    weak: 'bg-red-500',
    fair: 'bg-amber-400',
    good: 'bg-primary-500',
    strong: 'bg-green-500',
  }[level];

  const textColor = {
    none: 'text-surface-400',
    weak: 'text-red-500',
    fair: 'text-amber-500',
    good: 'text-primary-600',
    strong: 'text-green-600',
  }[level];

  const checkItems = [
    { key: 'length', label: '8+ characters' },
    { key: 'uppercase', label: 'Uppercase letter' },
    { key: 'lowercase', label: 'Lowercase letter' },
    { key: 'number', label: 'Number' },
    { key: 'special', label: 'Special character' },
  ];

  return (
    <div className="mt-3 space-y-2">
      {/* Bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-surface-200 rounded-full overflow-hidden">
          <motion.div
            className={clsx('h-full rounded-full', barColor)}
            animate={{ width }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          />
        </div>
        {text && (
          <span className={clsx('text-caption font-semibold w-12 text-right', textColor)}>
            {text}
          </span>
        )}
      </div>

      {/* Checklist */}
      {Object.keys(checks).length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {checkItems.map(({ key, label }) => (
            <div
              key={key}
              className={clsx(
                'flex items-center gap-1.5 text-caption transition-colors duration-200',
                checks[key] ? 'text-green-600' : 'text-surface-400'
              )}
            >
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                {checks[key] ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                ) : (
                  <circle cx="12" cy="12" r="9" strokeWidth={2} />
                )}
              </svg>
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Navigation buttons shared across steps. */
function StepNav({ onBack, onNext, onSubmit, isFirst, isLast, loading, nextLabel }) {
  return (
    <div className={clsx('flex gap-3 mt-6', isFirst ? 'justify-end' : 'justify-between')}>
      {!isFirst && (
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-surface-200 text-body-sm font-semibold text-surface-600 hover:bg-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-1 disabled:opacity-50 transition-colors duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      )}

      <button
        type={isLast ? 'submit' : 'button'}
        onClick={isLast ? onSubmit : onNext}
        disabled={loading}
        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 shadow-soft-sm"
      >
        {loading && (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {loading ? 'Creating account...' : (nextLabel || 'Continue')}
        {!loading && !isLast && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step panels
// ---------------------------------------------------------------------------

const stepVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -40 : 40,
    opacity: 0,
  }),
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function RegistrationForm() {
  const navigate = useNavigate();
  const { register } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // /account-type navigates here with { state: { accountType } }. This used to
  // hardcode 'user' and never read location.state, so the choice the user made
  // on that page was silently discarded and every signup became a personal
  // account -- business and organization registration was unreachable from that
  // entry point.
  const location = useLocation();
  const preselectedAccountType = location.state?.accountType;
  const [accountType, setAccountType] = useState(
    ['user', 'business', 'organization'].includes(preselectedAccountType)
      ? preselectedAccountType
      : 'user'
  );
  const [formData, setFormData] = useState({
    // Step 2 — credentials
    email: '',
    password: '',
    username: '',
    // Step 3 — personal profile
    firstName: '',
    lastName: '',
    bio: '',
    interests: '',
    // Step 3 — business / organization profile
    businessName: '',
    address: '',
    businessPhone: '',
    operatingHours: '',
    businessType: '',
    description: '',
  });

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change
    if (fieldErrors[name]) {
      setFieldErrors((prev) => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const goForward = () => {
    setDirection(1);
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  // -------------------------------------------------------------------------
  // Validation per step
  // -------------------------------------------------------------------------

  const validateStep2 = () => {
    const errors = {};
    if (!formData.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Enter a valid email address';
    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (!formData.username) errors.username = 'Username is required';
    else if (formData.username.length < 3) errors.username = 'Username must be at least 3 characters';
    return errors;
  };

  const validateStep3 = () => {
    const errors = {};
    if (accountType === 'user') {
      if (!formData.firstName) errors.firstName = 'First name is required';
      if (!formData.lastName) errors.lastName = 'Last name is required';
    } else {
      if (!formData.businessName) errors.businessName = `${accountType === 'business' ? 'Business' : 'Organization'} name is required`;
      if (!formData.address) errors.address = 'Address is required';
      if (!formData.businessPhone) errors.businessPhone = 'Phone number is required';
      if (!formData.businessType) errors.businessType = `${accountType === 'business' ? 'Business' : 'Organization'} type is required`;
      if (!formData.description) errors.description = 'Description is required';
    }
    return errors;
  };

  // -------------------------------------------------------------------------
  // Step handlers
  // -------------------------------------------------------------------------

  const handleStep1Next = () => {
    goForward();
  };

  const handleStep2Next = () => {
    const errors = validateStep2();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      toast.error('Please fix the highlighted fields before continuing.');
      return;
    }
    goForward();
  };

  const handleStep3Submit = async () => {
    const errors = validateStep3();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      toast.error('Please fix the highlighted fields before continuing.');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        username: formData.username,
        account_type: accountType,
        first_name: formData.firstName,
        last_name: formData.lastName,
      };

      if (accountType !== 'user') {
        userData.business_name = formData.businessName;
        userData.address = formData.address;
        userData.business_phone = formData.businessPhone;
        userData.operating_hours = formData.operatingHours;
        userData.business_type = formData.businessType;
        userData.description = formData.description;
      } else {
        userData.bio = formData.bio;
        userData.interests = formData.interests;
      }

      await register(userData);

      toast.success('Account created! Welcome to CliQQ.');
      setDirection(1);
      setStep(4);
    } catch (err) {
      toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const isBusinessType = accountType === 'business' || accountType === 'organization';
  const entityLabel = accountType === 'business' ? 'Business' : accountType === 'organization' ? 'Organization' : '';

  return (
    <div>
      <StepIndicator currentStep={step} totalSteps={STEPS.length} />

      <AnimatePresence mode="wait" custom={direction}>
        {/* ---------------------------------------------------------------- */}
        {/* STEP 1 — Account type                                            */}
        {/* ---------------------------------------------------------------- */}
        {step === 1 && (
          <motion.div
            key="step1"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <h2 className="text-h2 text-surface-900 mb-1">Choose account type</h2>
            <p className="text-body text-surface-500 mb-6">
              Select the type of account that best describes you.
            </p>

            <SegmentedControl
              name="accountType"
              label="Account type"
              options={ACCOUNT_TYPE_OPTIONS}
              value={accountType}
              onChange={setAccountType}
            />

            {/* Account type description cards */}
            <div className="mt-6 grid gap-3">
              {ACCOUNT_TYPE_OPTIONS.map((opt) => {
                const descriptions = {
                  user: 'Join events, follow people, and discover what\'s happening around you.',
                  business: 'Promote your business, host events, and reach local customers.',
                  organization: 'Run a club, charity, or community group and engage your members.',
                };
                const isSelected = accountType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAccountType(opt.value)}
                    className={clsx(
                      'w-full text-left p-4 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30',
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-surface-200 bg-white hover:border-surface-300'
                    )}
                    aria-pressed={isSelected}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={clsx(
                          'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
                          isSelected ? 'bg-primary-500 text-white' : 'bg-surface-100 text-surface-500'
                        )}
                      >
                        {opt.icon}
                      </div>
                      <div>
                        <p className={clsx('text-body-sm font-semibold', isSelected ? 'text-primary-700' : 'text-surface-800')}>
                          {opt.label}
                        </p>
                        <p className="text-caption text-surface-500">{descriptions[opt.value]}</p>
                      </div>
                      {isSelected && (
                        <div className="ml-auto">
                          <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <StepNav isFirst onNext={handleStep1Next} nextLabel="Continue" />
          </motion.div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* STEP 2 — Credentials                                             */}
        {/* ---------------------------------------------------------------- */}
        {step === 2 && (
          <motion.div
            key="step2"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <h2 className="text-h2 text-surface-900 mb-1">Your credentials</h2>
            <p className="text-body text-surface-500 mb-6">
              These are used to log in to your account.
            </p>

            <div className="space-y-4">
              <FloatingInput
                label="Email address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={fieldErrors.email}
                autoComplete="email"
              />

              <div>
                <FloatingInput
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  error={fieldErrors.password}
                  autoComplete="new-password"
                />
                {formData.password && <PasswordStrength password={formData.password} />}
              </div>

              <FloatingInput
                label="Username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                error={fieldErrors.username}
                autoComplete="username"
              />
            </div>

            <StepNav onBack={goBack} onNext={handleStep2Next} nextLabel="Continue" />
          </motion.div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* STEP 3 — Profile details                                         */}
        {/* ---------------------------------------------------------------- */}
        {step === 3 && (
          <motion.div
            key="step3"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <h2 className="text-h2 text-surface-900 mb-1">
              {accountType === 'user' ? 'Your profile' : `${entityLabel} profile`}
            </h2>
            <p className="text-body text-surface-500 mb-6">
              {accountType === 'user'
                ? 'Tell the community a little about yourself.'
                : `Tell the community about your ${accountType}.`}
            </p>

            <div className="space-y-4">
              {/* Personal user fields */}
              {accountType === 'user' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FloatingInput
                      label="First name"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      error={fieldErrors.firstName}
                      autoComplete="given-name"
                    />
                    <FloatingInput
                      label="Last name"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      error={fieldErrors.lastName}
                      autoComplete="family-name"
                    />
                  </div>

                  <div className="relative">
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      rows={3}
                      placeholder=" "
                      className="peer w-full bg-surface-50 border border-surface-200 rounded-xl text-surface-900 pt-6 pb-2 px-4 resize-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white focus:outline-none transition-all duration-200 text-body placeholder-transparent"
                    />
                    <label
                      htmlFor="bio"
                      className="absolute left-4 top-2 text-caption text-surface-500 pointer-events-none select-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400 transition-all duration-200"
                    >
                      Bio <span className="text-surface-400">(optional)</span>
                    </label>
                  </div>

                  <FloatingInput
                    label="Interests (optional, comma-separated)"
                    name="interests"
                    type="text"
                    value={formData.interests}
                    onChange={handleChange}
                    placeholder="e.g., hiking, photography, coffee"
                  />
                </>
              )}

              {/* Business / organization fields */}
              {isBusinessType && (
                <>
                  <FloatingInput
                    label={`${entityLabel} name`}
                    name="businessName"
                    type="text"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    error={fieldErrors.businessName}
                  />

                  <FloatingInput
                    label="Address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    error={fieldErrors.address}
                    autoComplete="street-address"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FloatingInput
                      label="Phone number"
                      name="businessPhone"
                      type="tel"
                      value={formData.businessPhone}
                      onChange={handleChange}
                      required
                      error={fieldErrors.businessPhone}
                      autoComplete="tel"
                    />
                    <FloatingInput
                      label="Operating hours"
                      name="operatingHours"
                      type="text"
                      value={formData.operatingHours}
                      onChange={handleChange}
                      placeholder="Mon–Fri 9am–5pm"
                    />
                  </div>

                  <FloatingInput
                    label={`${entityLabel} type`}
                    name="businessType"
                    type="text"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    error={fieldErrors.businessType}
                    placeholder={accountType === 'business' ? 'e.g., Restaurant, Retail' : 'e.g., Charity, Sports club'}
                  />

                  <div className="relative">
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      required
                      placeholder=" "
                      aria-invalid={fieldErrors.description ? 'true' : undefined}
                      className={clsx(
                        'peer w-full bg-surface-50 border rounded-xl text-surface-900 pt-6 pb-2 px-4 resize-none focus:ring-2 focus:bg-white focus:outline-none transition-all duration-200 text-body placeholder-transparent',
                        fieldErrors.description
                          ? 'border-error-500 focus:ring-error-500/20 focus:border-error-500'
                          : 'border-surface-200 focus:ring-primary-500/20 focus:border-primary-500'
                      )}
                    />
                    <label
                      htmlFor="description"
                      className={clsx(
                        'absolute left-4 top-2 pointer-events-none select-none transition-all duration-200',
                        'text-caption peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-body peer-placeholder-shown:text-surface-400',
                        fieldErrors.description ? 'text-error-500' : 'text-surface-500'
                      )}
                    >
                      Description <span className="text-error-500">*</span>
                    </label>
                    {fieldErrors.description && (
                      <p className="flex items-center gap-1.5 mt-1.5 px-1 text-caption text-error-600" role="alert">
                        {fieldErrors.description}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <StepNav
              onBack={goBack}
              isLast
              onSubmit={handleStep3Submit}
              loading={loading}
              nextLabel="Create account"
            />
          </motion.div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* STEP 4 — Success                                                 */}
        {/* ---------------------------------------------------------------- */}
        {step === 4 && (
          <motion.div
            key="step4"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="text-center py-8"
          >
            {/* Success icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
              className="w-20 h-20 rounded-full bg-primary-50 border-4 border-primary-500 flex items-center justify-center mx-auto mb-6"
            >
              <svg className="w-10 h-10 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>

            <h2 className="text-h1 text-surface-900 mb-2">You're in!</h2>
            <p className="text-body text-surface-500 mb-8 max-w-sm mx-auto">
              Your CliQQ account has been created. Dive in and start exploring events
              and connecting with your community.
            </p>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200 shadow-soft-md"
            >
              Go to home
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default RegistrationForm;
